import { datadogRum } from '@datadog/browser-rum';
import { useCallback, useEffect, useRef } from 'react';

/**
 * Measures how long an agent waits from opening an AD case link until the whole case page is
 * rendered, including the Microsoft login redirect and the second document load it causes.
 * Reported to Datadog RUM as the duration vital `ad_case_ready`.
 */
export const AD_CASE_READY_VITAL = 'ad_case_ready';

export type AdCaseReadyStep = 'case' | 'questionnaire' | 'qualification' | 'lost_reasons';
export type AdCaseReadyOutcome = 'ready' | 'error' | 'conflict' | 'timeout' | 'abandoned';

type AdCaseReadyStart = { startTime: number; documentLoads: number };

const startKey = (caseId: string) => `booking-os-ad-ready-start:${caseId.toLowerCase()}`;
// A start older than this belongs to an earlier visit of the same case in this tab.
const MAX_START_AGE_MS = 5 * 60 * 1000;
const TIMEOUT_MS = 60 * 1000;

const readStart = (caseId: string): AdCaseReadyStart | null => {
  try {
    const saved = sessionStorage.getItem(startKey(caseId));
    return saved ? (JSON.parse(saved) as AdCaseReadyStart) : null;
  } catch {
    return null;
  }
};

/**
 * Runs on every document load before authentication. The first load of an AD case link starts the
 * clock; loads that follow within the same tab (the login redirect) only increase the count.
 * sessionStorage survives the redirect to Microsoft and back.
 */
export const recordAdCaseDocumentLoad = (location: Pick<Location, 'pathname' | 'search'> = window.location) => {
  const params = new URLSearchParams(location.search);
  const caseId = params.get('id')?.trim();
  if (location.pathname !== '/booking' || params.get('type') !== 'ad' || !caseId) return;
  try {
    const saved = readStart(caseId);
    const start: AdCaseReadyStart = saved && Date.now() - saved.startTime < MAX_START_AGE_MS
      ? { ...saved, documentLoads: saved.documentLoads + 1 }
      : { startTime: Math.round(performance.timeOrigin), documentLoads: 1 };
    sessionStorage.setItem(startKey(caseId), JSON.stringify(start));
  } catch {
    // Without storage the visit is simply not measured.
  }
};

export type AdCaseReadyTimer = {
  markStep: (step: AdCaseReadyStep) => void;
  /** Reports `ready` once every required step is marked and the browser has painted that frame. */
  finishWhenPainted: (required: AdCaseReadyStep[]) => void;
  finish: (outcome: AdCaseReadyOutcome) => void;
  /** Stops listening without reporting, e.g. when the page unmounts. */
  dispose: () => void;
};

export const createAdCaseReadyTimer = (caseId: string): AdCaseReadyTimer => {
  const start = readStart(caseId);
  const steps: Partial<Record<AdCaseReadyStep, number>> = {};
  let done = !start;
  let paintScheduled = false;
  let wasHidden = document.visibilityState === 'hidden';

  const onVisibilityChange = () => {
    if (document.visibilityState !== 'hidden') return;
    wasHidden = true;
    // A hidden tab never paints, so a pending ready report would otherwise wait for the timeout.
    if (paintScheduled) finish('ready');
  };
  const onPageHide = () => finish('abandoned');
  const timeout = start
    ? window.setTimeout(() => finish('timeout'), Math.max(0, start.startTime + TIMEOUT_MS - Date.now()))
    : undefined;

  const dispose = () => {
    window.clearTimeout(timeout);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('pagehide', onPageHide);
  };

  const finish = (outcome: AdCaseReadyOutcome) => {
    if (done || !start) return;
    done = true;
    dispose();
    try {
      sessionStorage.removeItem(startKey(caseId));
    } catch { /* storage may be unavailable */ }
    const now = Date.now();
    // RUM discards events that start before the current document's view. After the login redirect
    // the clock started on an earlier document, so the vital is anchored at the report time and
    // carries the full duration.
    datadogRum.addDurationVital(AD_CASE_READY_VITAL, {
      startTime: now,
      duration: now - start.startTime,
      context: {
        outcome,
        steps_ms: steps,
        document_loads: start.documentLoads,
        login_redirect: start.documentLoads > 1,
        was_hidden: wasHidden,
      },
    });
  };

  if (start) {
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);
  }

  return {
    markStep: (step) => {
      if (!done && steps[step] === undefined) steps[step] = Date.now() - start!.startTime;
    },
    finishWhenPainted: (required) => {
      if (done || paintScheduled || !required.every((step) => steps[step] !== undefined)) return;
      paintScheduled = true;
      if (document.visibilityState === 'hidden') {
        finish('ready');
        return;
      }
      // The timeout callback runs after the frame requested here has been painted.
      requestAnimationFrame(() => window.setTimeout(() => finish('ready')));
    },
    finish,
    dispose,
  };
};

type AdCaseReadyPageState = {
  caseLoaded: boolean;
  /** False when the page cannot show the questionnaire (no saved form, no questions or a locked case). */
  formExpected: boolean;
  lostReasonsReady: boolean;
  failure?: 'error' | 'conflict';
};

/** Wires the timer to the AD booking page. Returns the callback for steps reported by child components. */
export const useAdCaseReadyTiming = (caseId: string, page: AdCaseReadyPageState) => {
  const timerRef = useRef<AdCaseReadyTimer | null>(null);
  const requiredRef = useRef<AdCaseReadyStep[]>([]);
  requiredRef.current = page.formExpected
    ? ['case', 'questionnaire', 'qualification', 'lost_reasons']
    : ['case', 'lost_reasons'];

  useEffect(() => {
    const timer = createAdCaseReadyTimer(caseId);
    timerRef.current = timer;
    return () => {
      timer.dispose();
      if (timerRef.current === timer) timerRef.current = null;
    };
  }, [caseId]);

  useEffect(() => {
    const timer = timerRef.current;
    if (!timer) return;
    if (page.failure) {
      timer.finish(page.failure);
      return;
    }
    if (page.caseLoaded) timer.markStep('case');
    if (page.lostReasonsReady) timer.markStep('lost_reasons');
    timer.finishWhenPainted(requiredRef.current);
  }, [page.caseLoaded, page.formExpected, page.lostReasonsReady, page.failure]);

  return useCallback((step: AdCaseReadyStep) => {
    timerRef.current?.markStep(step);
    timerRef.current?.finishWhenPainted(requiredRef.current);
  }, []);
};
